import { getStore, getStoreChangeHandler } from '../uui-context/uui-context';

export const LATEST_THEME_VERSION = '2.0';

export interface UuiTheme {
  name: string;
}

const THEMEABLE_KEY = '_uui_themeable';
const THEME_ATTRIBUTE = 'data-theme';

export function getThemeName(
  el: HTMLElement,
  defaultTheme = 'default'
): string {
  let theme = el?.dataset?.theme;
  while (!theme && el) {
    theme = el?.dataset?.theme;
    if (theme) {
      return theme;
    } else {
      el = el.parentElement;
    }
  }
  return theme || document.documentElement.dataset?.theme || defaultTheme;
}

export function getThemes() {
  return getStore().themes;
}

export function getThemeNames(includeInternal = false) {
  let themeNames = [...Object.keys(getThemes())];
  if (!includeInternal) {
    const internalNames = ['global-variables', 'base-styles'];
    for (const internalName of internalNames) {
      const found = themeNames.indexOf(internalName);
      if (found >= 0) {
        themeNames.splice(found, 1);
      }
    }
  }
  themeNames.sort();
  return themeNames;
}

export function getThemeStyles(name: string): HTMLStyleElement[] | undefined {
  return getThemes()[name];
}

export function clearThemeStyles(name: string) {
  const themes = getThemes();
  const styles = themes[name];
  if (styles?.length) {
    for (const style of styles) {
      style.parentNode?.removeChild(style);
    }
    styles.length = 0;
  }
  delete themes[name];
  getStore().themeRevision++;
}

export function setTheme(name: string, root?: HTMLElement) {
  root = root ?? document.documentElement;
  if (name === 'default') {
    root.removeAttribute(THEME_ATTRIBUTE);
  } else {
    root.setAttribute(THEME_ATTRIBUTE, name);
  }
  getStore().themeRevision++;
}

export function setThemeStyles({
  name,
  version,
  themeStyles,
  global
}: {
  name: string;
  version?: string;
  themeStyles: HTMLStyleElement[];
  global: boolean;
}) {
  if (!themeStyles) {
    return;
  }
  const themes = getThemes();
  let styles = themes[name];
  if (!styles) {
    styles = [];
    themes[name] = styles;
  }
  styles.push(...themeStyles);
  if (global && document?.head) {
    let changed = false;
    for (let i = 0; i < themeStyles.length; i++) {
      const style = themeStyles[i];
      const nameAndVersion = version ? `${name}-${version}` : `${name}`;
      const themeId = `theme-${nameAndVersion}-${i}`;
      if (style && !document.getElementById(themeId)) {
        style.id = themeId;
        document.head.appendChild(style);
        changed = true;
      }
    }
    if (changed) {
      getStore().themeRevision++;
    }
  }
}

function ensureTheme(name: string, version?: string) {
  const themeId = `theme-${name}`;
  const themeStyles = getThemeStyles(name);
  if (!themeStyles && !document.getElementById(themeId)) {
    document.head.appendChild(
      document.createElement(`uui-${name}-theme-${version}`)
    );
  }
}

export function ensureDefaultTheme() {
  ensureTheme('global-variables');
  ensureTheme('default', LATEST_THEME_VERSION);
}

export function getApplyThemeFunction(component, elementFetcher) {
  const existingHandler = component.componentDidLoad;
  return () => {
    const el = elementFetcher(component);
    if (el && el.shadowRoot) {
      const shadowRoot = el.shadowRoot;
      const themeName = getThemeName(el);
      for (const shadowChild of shadowRoot.children) {
        if (shadowChild.nodeName === 'STYLE') {
          // Do not theme style nodes. That's silly.
          continue;
        }
        shadowChild.setAttribute('data-theme', themeName);
      }
      const themeStyles = getThemeStyles(themeName);
      if (themeStyles?.length) {
        let i = 0;
        for (const themeStyle of themeStyles) {
          const styleId = `theme_${themeName}_${i++}`;
          const existingStyle = shadowRoot.getElementById(styleId);
          if (!existingStyle) {
            const styleNode = themeStyle.cloneNode(true) as HTMLStyleElement;
            styleNode.id = styleId;
            shadowRoot.appendChild(styleNode);
          }
        }
      }
    }
    if (existingHandler) {
      existingHandler.call(component);
    }
  };
}

export function makeThemeable(component, elementFetcher = c => c.el) {
  if (component && !component.hasOwnProperty(THEMEABLE_KEY)) {
    component[THEMEABLE_KEY] = true;
    const onChange = getStoreChangeHandler();
    onChange('themeRevision', () => {
      component.componentDidLoad();
    });

    component.componentDidLoad = getApplyThemeFunction(
      component,
      elementFetcher
    );
  }
}
